import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextEyePassword } from "./TextEyePassword";
import { Box } from "@mui/system";
import {
  IconButton,
  VaporIcon,
  Drawer,
  Title,
  VaporToolbar,
  Button,
  Spinner,
  NotificationOverflow,
  NotificationInline,
  AlertTitle,
  Toast,
} from "@vapor/v3-components";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { getLogonInfoFromCookies, saveLogonInfoToCookies } from "../utils/util";
import {
  ChangePasswordResponse,
  LocalLogin,
} from "../services/Models/AuthModel";
import { changePassword } from "../services/changePasswordService";
import { useWindowWidth } from "../utils/useWindowWidth";
import { returnError } from "../utils/util";

interface ChangePasswordProps {
  title: string;
  open: boolean;
  onClose: () => void;
  passwordExpired?: boolean;
  userName?: string;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({
  title,
  open,
  onClose,
  passwordExpired,
  userName,
}) => {
  // const loginResult = useRef<AuthResponse>(getLogonInfoFromCookies());
  const { t } = useTranslation();

  const windowWidth = useWindowWidth(); // Larghezza della finestra
  const drawerWidth =
    windowWidth > 450 ? Math.max(windowWidth * 0.3, 450) : windowWidth;

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [renewPassword, setRenewPassword] = useState("");
  const [activeSave, setActiveSave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveOK, setSaveOK] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const callChangePassword = async () => {
    let loginResult: LocalLogin | undefined = undefined;
    if (!passwordExpired) loginResult = getLogonInfoFromCookies();
    else
      loginResult = {
        SecurityToken: "expired",
        UserLogin: userName ?? "",
        rememberMe: false,
        Image: "",
        Language: "it",
        Warning: "",
        DetailedMessage: "",
        MessageNumber: 0,
        ResponseTimeStamp: new Date(),
        ResultCode: "",
      };

    if (loginResult && loginResult.SecurityToken && !loading) {
      setLoading(true);
      try {
        const result: ChangePasswordResponse = await changePassword(
          loginResult.SecurityToken,
          loginResult.UserLogin,
          oldPassword,
          newPassword
        );
        if (result && result.ResultCode !== "ERROR") {
          console.log("getFilter successful:");
          loginResult.SecurityToken = result.NewSecurityToken;
          saveLogonInfoToCookies({
            ...loginResult,
            SecurityToken: result.NewSecurityToken,
          });
          setSaveOK(t("change-password"));
          onClose();
        } else {
          setError(t(returnError(result)));
          console.error("callChangePassword Error :", result.DetailedMessage);
        }
      } catch (error) {
        setError(t("errore interno"));
        console.error("callChangePassword Error :", error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Drawer anchor="right" hideBackdrop width={drawerWidth} open={open}>
      <Title
        divider
        rightItems={[
          <IconButton color="primary" onClick={onClose} variant="text">
            <VaporIcon icon={faXmark} size="s" color="primary" />
          </IconButton>,
        ]}
        title={title}
      />
      <Box padding={2} margin={2}>
        <TextEyePassword
          variant="outlined"
          label={t("old-password-key")}
          helperText={t("old-password-help-descr")}
          id="oldPassword"
          style={{
            width: 400,
          }}
          value={oldPassword}
          onChange={(e) => {
            if (oldPassword !== e.target.value) setActiveSave(true);
            setOldPassword(e.target.value);
          }}
          autoFocus
        />
        <TextEyePassword
          variant="outlined"
          label={t("new-password-key1")}
          helperText={t("new-password-help-descr1")}
          id="newPassword"
          style={{
            marginTop: 32,
            width: 400,
          }}
          value={newPassword}
          onChange={(e) => {
            if (newPassword !== e.target.value) setActiveSave(true);
            setNewPassword(e.target.value);
          }}
        />
        <TextEyePassword
          variant="outlined"
          label={t("new-password-key2")}
          helperText={
            renewPassword !== ""
              ? newPassword !== renewPassword
                ? t("no-password-match")
                : t("new-password-help-descr2")
              : t("new-password-help-descr2")
          }
          id="renewPassword"
          style={{
            marginTop: 32,
            width: 400,
          }}
          value={renewPassword}
          error={
            renewPassword !== ""
              ? newPassword !== renewPassword
                ? true
                : undefined
              : undefined
          }
          onChange={(e) => {
            if (renewPassword !== e.target.value) setActiveSave(true);
            setRenewPassword(e.target.value);
          }}
        />
      </Box>
      <VaporToolbar
        contentRight={[
          <Button variant="outlined" onClick={onClose}>
            {t("cancel")}
          </Button>,
          <Button
            variant="contained"
            onClick={() => {
              callChangePassword();
            }}
            disabled={!activeSave || newPassword !== renewPassword}
          >
            {t("change")}
          </Button>,
        ]}
        size="medium"
        variant="regular"
        withoutAppBar
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)", // Sfondo semitrasparente opzionale
          }}
        >
          <Spinner size={64} />
        </Box>
      )}
      {error && (
        <NotificationOverflow severity="error" onClose={() => setError(null)}>
          <AlertTitle>{t("error-header")}</AlertTitle>
          {error}
        </NotificationOverflow>
      )}
      <Toast
        open={saveOK ? true : false}
        anchorOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        autoHideDuration={2000}
        onClose={() => setSaveOK(null)}
      >
        <NotificationInline severity="success" onClose={() => setSaveOK(null)}>
          <AlertTitle>{saveOK}</AlertTitle>
          {t("changePwdOK")}
        </NotificationInline>
      </Toast>
    </Drawer>
  );
};
